import React, { useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';

export type BulkProgressEntryState = 'pending' | 'active' | 'ok' | 'fail';

export type BulkProgressEntry = {
  key: string;
  name: string;
  state: BulkProgressEntryState;
  note?: string;
};

export type BulkProgressState = {
  phase: 'scrape' | 'scrape_retry' | 'save';
  current: number;
  total: number;
  currentLabel: string;
  entries: BulkProgressEntry[];
};

type Props = {
  progress: BulkProgressState | null;
};

const phaseTitle: Record<BulkProgressState['phase'], string> = {
  scrape: 'Toplu veri çekiliyor',
  scrape_retry: 'Başarısız forumlar yeniden deneniyor (2. tur)',
  save: 'Değişiklikler kaydediliyor',
};

const BulkScrapeProgressDialog: React.FC<Props> = ({ progress }) => {
  const listRef = useRef<HTMLDivElement>(null);
  const open = progress !== null && progress.total > 0;

  const pct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.current / progress.total) * 100))
      : 0;

  const counts = { ok: 0, fail: 0, active: 0, pending: 0 };
  if (progress) {
    for (const e of progress.entries) {
      counts[e.state] += 1;
    }
  }

  useEffect(() => {
    if (!progress || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [progress?.current, progress?.entries]);

  if (!progress) return null;

  return (
    <Dialog open={open} onClose={() => {}} className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />

        <div className="relative w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-gray-800">
          <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              {phaseTitle[progress.phase]}
            </Dialog.Title>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {progress.currentLabel
                ? `Şu an: ${progress.currentLabel}`
                : 'Hazırlanıyor…'}
            </p>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>
                  {progress.current} / {progress.total} forum
                </span>
                <span>%{pct}</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-teal-500 transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-green-700 dark:bg-green-950/40 dark:text-green-300">
                <CheckCircle2 size={13} /> Sorunsuz: {counts.ok}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                <XCircle size={13} /> Hatalı: {counts.fail}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300">
                <Loader2 size={13} className="animate-spin" /> İşlemde: {counts.active}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                <Circle size={13} /> Bekleyen: {counts.pending}
              </span>
            </div>
          </div>

          <div
            ref={listRef}
            className="max-h-[min(52vh,420px)] overflow-y-auto px-3 py-3 space-y-1"
            aria-live="polite"
            aria-label="İlerleme listesi"
          >
            {progress.entries.map((entry) => (
              <div
                key={entry.key}
                data-active={entry.state === 'active' ? 'true' : undefined}
                className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${
                  entry.state === 'active'
                    ? 'bg-teal-50 dark:bg-teal-950/40'
                    : 'bg-gray-50 dark:bg-gray-900/40'
                }`}
              >
                <span className="shrink-0 mt-0.5">
                  {entry.state === 'active' ? (
                    <Loader2 size={16} className="animate-spin text-teal-600" />
                  ) : entry.state === 'ok' ? (
                    <CheckCircle2 size={16} className="text-green-500" />
                  ) : entry.state === 'fail' ? (
                    <XCircle size={16} className="text-red-500" />
                  ) : (
                    <Circle size={16} className="text-gray-300 dark:text-gray-600" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100 block truncate">
                    {entry.name}
                  </span>
                  {entry.note && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                      {entry.note}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-200 px-5 py-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
            İşlem sürerken pencereyi kapatmayın. Liste otomatik kaydırılır.
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default BulkScrapeProgressDialog;
